import { useCallback, useEffect, useRef, useState } from 'react';
import { catalogSavesStorageKey } from '../shared/catalogLookbook';

const readSavedIds = (storageKey: string): Set<string> => {
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) {
      return new Set();
    }
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? new Set(parsed.filter((id) => typeof id === 'string')) : new Set();
  } catch (error) {
    console.error('Failed to load saved catalog looks', { storageKey, error });
    return new Set();
  }
};

const persistSavedIds = (storageKey: string, ids: Set<string>) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(Array.from(ids)));
  } catch (error) {
    console.error('Failed to persist saved catalog looks', { storageKey, error });
  }
};

export const useCatalogSaves = (catalogUuid?: string) => {
  const storageKey = catalogUuid ? catalogSavesStorageKey(catalogUuid) : null;
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [hydratedKey, setHydratedKey] = useState<string | null>(null);
  const hydratedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!storageKey) {
      hydratedKeyRef.current = null;
      setHydratedKey(null);
      setSavedIds(new Set());
      return;
    }
    const stored = readSavedIds(storageKey);
    if (hydratedKeyRef.current == null) {
      setSavedIds((current) => {
        if (current.size === 0) {
          return stored;
        }
        const merged = new Set(stored);
        current.forEach((id) => merged.add(id));
        return merged;
      });
    } else if (hydratedKeyRef.current !== storageKey) {
      setSavedIds(stored);
    }
    hydratedKeyRef.current = storageKey;
    setHydratedKey(storageKey);
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || hydratedKey !== storageKey) {
      return;
    }
    persistSavedIds(storageKey, savedIds);
  }, [savedIds, storageKey, hydratedKey]);

  const toggleSave = useCallback((itemUuid?: string) => {
    if (!itemUuid) {
      return;
    }
    setSavedIds((current) => {
      const next = new Set(current);
      if (next.has(itemUuid)) {
        next.delete(itemUuid);
      } else {
        next.add(itemUuid);
      }
      if (storageKey) {
        persistSavedIds(storageKey, next);
      }
      return next;
    });
  }, [storageKey]);

  const isSaved = useCallback((itemUuid?: string) => Boolean(itemUuid && savedIds.has(itemUuid)), [savedIds]);

  return {
    savedIds,
    toggleSave,
    isSaved,
    savedCount: savedIds.size,
  };
};
